import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { TextDefault } from '@awarego-ui/components';
import { formatDate } from '../utils/helpers';

function buildSubscriptionIssues({
    currentCompany,
    haveValidPackage,
    havePartnerSubscription,
    subscriptionNotStarted,
}) {
    const issues = [];

    if (!haveValidPackage && currentCompany) {
        const packageLabel = currentCompany?.subscription?.package_trial
            ? 'free trial'
            : 'subscription';

        if (subscriptionNotStarted) {
            issues.push(
                `Your ${packageLabel} has not started yet and is set to start on ${formatDate(
                    currentCompany.subscription.package_valid_from
                )}.`
            );
        } else if (havePartnerSubscription) {
            issues.push('Your subscription has expired.');
        } else {
            issues.push(`Your ${packageLabel} has expired.`);
        }
    }

    if (currentCompany && currentCompany.usersLeft < 0) {
        issues.push(
            `You have registered more employees (${currentCompany.users_total}) than your subscription package allows (${currentCompany.users_available}).`
        );
    }

    return issues;
}

function shouldShowContactFooter({
    issues,
    subscriptionNotStarted,
    haveValidPackage,
    currentCompany,
}) {
    if (!issues.length) return false;
    if (
        issues.length === 1 &&
        subscriptionNotStarted &&
        !haveValidPackage &&
        !(currentCompany && currentCompany.usersLeft < 0)
    ) {
        return false;
    }
    return true;
}

function DefaultContactFooter({
    currentCompany,
    supportEmail,
    name,
    havePartnerSubscription,
    hasOverLimit,
}) {
    const parts = [];

    if (!havePartnerSubscription && currentCompany?.chargebeeEnabled) {
        parts.push(
            <Fragment key="upgrade">
                Please <Link to="/subscriptions/upgrade">upgrade</Link> your{' '}
                {name} subscription.
            </Fragment>
        );
    } else {
        const email =
            supportEmail && Array.isArray(supportEmail)
                ? supportEmail.join(', ')
                : supportEmail;
        parts.push(
            <Fragment key="contact">
                Contact{' '}
                <a href={`mailto:${email}`}>
                    <strong>
                        <u>{email}</u>
                    </strong>
                </a>{' '}
                to upgrade.
            </Fragment>
        );
    }

    if (hasOverLimit) {
        if (currentCompany?.chargebeeEnabled) {
            parts.push(
                <Fragment key="change-subscription">
                    <Link to="/settings/subscriptions">Change subscription</Link>
                </Fragment>
            );
        }
        parts.push(
            <Fragment key="edit-users">
                <Link to="/users">Edit employee lists</Link>
            </Fragment>
        );
    }

    return parts.reduce((acc, part, index) => {
        if (index === 0) return [part];
        return [...acc, ' or ', part];
    }, []);
}

function SubscriptionStatusBanner({
    currentCompany,
    haveValidPackage,
    havePartnerSubscription,
    subscriptionNotStarted,
    supportEmail,
    contactMessage,
    name,
}) {
    const [isOpen, setIsOpen] = useState(true);

    const issues = buildSubscriptionIssues({
        currentCompany,
        haveValidPackage,
        havePartnerSubscription,
        subscriptionNotStarted,
    });

    if (!issues.length) return null;

    const hasOverLimit = currentCompany && currentCompany.usersLeft < 0;
    const showContact = shouldShowContactFooter({
        issues,
        subscriptionNotStarted,
        haveValidPackage,
        currentCompany,
    });

    return (
        <Alert
            color="warning"
            isOpen={isOpen}
            toggle={() => setIsOpen(false)}
        >
            {issues.map((issue, index) => (
                <Fragment key={issue}>
                    {index > 0 && <br />}
                    <TextDefault bold>{issue}</TextDefault>
                </Fragment>
            ))}
            {showContact && (
                <>
                    <br />
                    <TextDefault bold>
                        {contactMessage ? (
                            <Fragment>
                                {contactMessage}
                                {hasOverLimit &&
                                    !/edit employee lists/i.test(
                                        contactMessage
                                    ) && (
                                        <>
                                            {' '}
                                            <Link to="/users">
                                                Edit employee lists
                                            </Link>
                                        </>
                                    )}
                            </Fragment>
                        ) : (
                            <DefaultContactFooter
                                currentCompany={currentCompany}
                                supportEmail={supportEmail}
                                name={name}
                                havePartnerSubscription={
                                    havePartnerSubscription
                                }
                                hasOverLimit={hasOverLimit}
                            />
                        )}
                    </TextDefault>
                </>
            )}
        </Alert>
    );
}

export default SubscriptionStatusBanner;
